import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PasswordReset = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp' | 'newPassword' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOTP = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();

      // Store OTP temporarily
      localStorage.setItem(`reset_otp_${email}`, JSON.stringify({
        code: otp,
        timestamp: Date.now(),
        email
      }));

      // Send OTP via email (using our existing endpoint)
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          code: otp,
          subject: 'Reset Your FutrBet Password',
          message: `Your password reset code is: ${otp}\n\nThis code expires in 10 minutes.`
        })
      });

      if (response.ok) {
        setStep('otp');
      } else {
        throw new Error('Failed to send OTP');
      }
    } catch (error) {
      setError('Failed to send reset code. Please try again.');
      console.error('OTP send error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = () => {
    const stored = localStorage.getItem(`reset_otp_${email}`);
    if (!stored) {
      setError('No reset code found. Please request a new one.');
      return;
    }

    const data = JSON.parse(stored);
    const isExpired = Date.now() - data.timestamp > 10 * 60 * 1000; // 10 minutes

    if (isExpired) {
      setError('Reset code expired. Please request a new one.');
      localStorage.removeItem(`reset_otp_${email}`);
      setStep('email');
      return;
    }

    if (data.code === otpCode) {
      setStep('newPassword');
      localStorage.removeItem(`reset_otp_${email}`);
    } else {
      setError('Invalid code. Please try again.');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // In a real app, this would update the password in the database
      // For now, we'll just update it in localStorage
      const users = JSON.parse(localStorage.getItem('futrbet_users') || '{}');
      if (users[email]) {
        users[email].password = btoa(newPassword); // Simple encoding (use bcrypt in production!)
        localStorage.setItem('futrbet_users', JSON.stringify(users));
      }

      setStep('success');
    } catch (error) {
      setError('Failed to reset password. Please try again.');
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="border border-green-400 rounded-lg p-8 bg-black">
          <h1 className="text-2xl mb-6 text-center">
            🔐 PASSWORD RESET
          </h1>

          {/* Step indicator */}
          <div className="flex justify-between mb-8 text-xs">
            <span className={step === 'email' ? 'text-green-400' : 'text-gray-600'}>
              1. Email
            </span>
            <span className={step === 'otp' ? 'text-green-400' : 'text-gray-600'}>
              2. Verify
            </span>
            <span className={step === 'newPassword' ? 'text-green-400' : 'text-gray-600'}>
              3. New Password
            </span>
            <span className={step === 'success' ? 'text-green-400' : 'text-gray-600'}>
              4. Done
            </span>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900 border border-red-400 text-red-400 rounded">
              {error}
            </div>
          )}

          {/* Email Step */}
          {step === 'email' && (
            <div>
              <p className="mb-4 text-sm text-gray-400">
                Enter your email and we'll send you a code to reset your password.
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full p-3 bg-black border border-green-400 text-green-400 rounded mb-4 focus:outline-none focus:border-green-300"
                onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
              />
              <button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full py-3 bg-green-400 text-black font-bold rounded hover:bg-green-300 disabled:opacity-50 transition-colors"
              >
                {loading ? 'SENDING...' : 'SEND RESET CODE'}
              </button>
            </div>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <div>
              <p className="mb-4 text-sm text-gray-400">
                Enter the 6-digit code sent to {email}
              </p>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full p-3 bg-black border border-green-400 text-green-400 rounded mb-4 focus:outline-none focus:border-green-300 text-center text-2xl tracking-widest"
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyOTP()}
              />
              <button
                onClick={handleVerifyOTP}
                className="w-full py-3 bg-green-400 text-black font-bold rounded hover:bg-green-300 transition-colors"
              >
                VERIFY CODE
              </button>
              <button
                onClick={() => setStep('email')}
                className="w-full mt-2 py-2 text-gray-500 hover:text-green-400 transition-colors"
              >
                ← Back to email
              </button>
            </div>
          )}

          {/* New Password Step */}
          {step === 'newPassword' && (
            <div>
              <p className="mb-4 text-sm text-gray-400">
                Choose a new password (minimum 8 characters)
              </p>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="w-full p-3 bg-black border border-green-400 text-green-400 rounded mb-3 focus:outline-none focus:border-green-300"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="w-full p-3 bg-black border border-green-400 text-green-400 rounded mb-4 focus:outline-none focus:border-green-300"
                onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
              />
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full py-3 bg-green-400 text-black font-bold rounded hover:bg-green-300 disabled:opacity-50 transition-colors"
              >
                {loading ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-xl mb-4">Password Reset Successful!</h2>
              <p className="text-gray-400 mb-6">
                Your password has been updated successfully.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/ultra-auth')}
                  className="w-full py-3 bg-green-400 text-black font-bold rounded hover:bg-green-300 transition-colors"
                >
                  LOGIN WITH NEW PASSWORD
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 border border-green-400 text-green-400 rounded hover:bg-green-900 transition-colors"
                >
                  GO TO HOME
                </button>
              </div>
            </div>
          )}

          {/* Alternative Options */}
          <div className="mt-8 pt-6 border-t border-gray-700 text-center">
            <p className="text-xs text-gray-500 mb-2">
              Or use passwordless login
            </p>
            <a
              href="/otp-login"
              className="text-green-400 hover:text-green-300 text-sm underline"
            >
              Login with OTP instead →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;